'use client'

import { useEffect, useRef, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useModalStore } from '@/stores/useModalStore'
import Button, { ButtonSize } from '@/components/UI/Buttons/Button/Button'
import styles from './SpeedTest.module.css'
import { supportLinks } from '@/utils/constants'

declare global {
  interface Window {
    Speedtest?: new () => unknown
  }
}

interface SpeedTestProps {
  closing?: boolean
}

interface SpeedTestUpdate {
  dlStatus: number | string
  ulStatus: number | string
  pingStatus: number | string
  jitterStatus: number | string
  testState: number
}

interface SpeedTestClient {
  setParameter: (name: string, value: string) => void
  start: () => void
  abort: () => void
  onupdate: ((data: SpeedTestUpdate) => void) | null
  onend: ((data: unknown) => void) | null
}

interface SpeedTestProgressProps {
  type: string
  value: number
  units: string
}

/** Renders one of the two semicircular download/upload gauges. */
function SpeedTestProgress ({ type, value, units }: SpeedTestProgressProps) {
  const steps = 5
  const stepValue = 100 / steps
  const stepDegValue = 270 / steps
  const initialRotation = 225
  const stepsInfo = [{ rotateParent: initialRotation, rotateValue: 360 - initialRotation, value: 0 }]

  for (let index = 1; index <= steps; index++) {
    const rotateParent = initialRotation + index * stepDegValue
    stepsInfo.push({ rotateParent, rotateValue: 360 - rotateParent, value: index * stepValue })
  }

  return (
    <div className={styles.container}>
      <svg className={styles.circleContainer} viewBox='2 -2 28 36' xmlns='http://www.w3.org/2000/svg' aria-label={`${type}: ${value} ${units}`}>
        <linearGradient id={`speed-test-gradient-${type}`}>
          <stop className={styles.stop1} offset='70%' />
          <stop className={styles.stop2} offset='85%' />
          <stop className={styles.stop3} offset='100%' />
        </linearGradient>
        <circle className={styles.circleContainerBackground} r='16' cx='16' cy='16' shapeRendering='geometricPrecision' />
        <circle
          className={styles.circleContainerProgress}
          r='16'
          cx='16'
          cy='16'
          style={{
            strokeDashoffset: 0,
            strokeDasharray: `${Math.max(0, Math.min(100, Math.floor(value))) * 0.75} 100`,
            stroke: `url(#speed-test-gradient-${type})`
          }}
          shapeRendering='geometricPrecision'
        />
      </svg>
      <div className={styles.info}>
        <h3>{`${value} ${units}`}</h3>
        <p>{type}</p>
      </div>
      <div className={styles.indicators}>
        {stepsInfo.map((step) => (
          <div key={step.rotateParent} style={{ transform: `rotateZ(${step.rotateParent}deg)` }}>
            <p style={{ transform: `rotate(${step.rotateValue}deg)` }}>{step.value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function SpeedTest ({ closing = false }: SpeedTestProps) {
  const t = useTranslations("Speedtest")
  const locale = useLocale()
  const closeModal = useModalStore((state) => state.closeModal)
  const regularLatencyLimit = Number(process.env.NEXT_PUBLIC_SPEEDTEST_REGULAR_PING ?? process.env.SPEEDTEST_REGULAR_PING ?? 80)
  const badLatencyLimit = Number(process.env.NEXT_PUBLIC_SPEEDTEST_BAD_PING ?? process.env.SPEEDTEST_BAD_PING ?? 150)
  const jitterLimit = Number(process.env.NEXT_PUBLIC_SPEEDTEST_JITTER_REGULAR_LIMIT ?? process.env.SPEEDTEST_JITTER_REGULAR_LIMIT ?? 30)
  const badJitterLimit = Number(process.env.NEXT_PUBLIC_SPEEDTEST_JITTER_BAD_LIMIT ?? process.env.SPEEDTEST_JITTER_BAD_LIMIT ?? 60)
  const [started, setStarted] = useState(false)
  const [running, setRunning] = useState(false)
  const [download, setDownload] = useState(0)
  const [upload, setUpload] = useState(0)
  const [ping, setPing] = useState(0)
  const [jitter, setJitter] = useState(0)
  const speedTest = useRef<SpeedTestClient | null>(null)
  const runningRef = useRef(false)

  // The legacy script exposes Speedtest after the marker element is present.
  function getSpeedTest () {
    if (!speedTest.current && document.getElementById('speedtest') !== null && window.Speedtest !== undefined) {
      speedTest.current = new window.Speedtest() as SpeedTestClient
    }
    return speedTest.current
  }

  // Keep the old 500 ms close transition and abort an active network test first.
  useEffect(() => {
    if (!closing || !speedTest.current) return

    speedTest.current.onupdate = null
    if (runningRef.current) speedTest.current.abort()
    const timeout = window.setTimeout(closeModal, 500)
    return () => window.clearTimeout(timeout)
  }, [closing, closeModal])

  // Detach callbacks and abort the test when the modal component unmounts.
  useEffect(() => {
    return () => {
      if (speedTest.current) {
        speedTest.current.onupdate = null
        if (runningRef.current) speedTest.current.abort()
      }
    }
  }, [])

  function startTest () {
    if (running) return
    setStarted(true)
    setRunning(true)
    runningRef.current = true
    const client = getSpeedTest()
    if (!client) {
      setRunning(false)
      runningRef.current = false
      return
    }

    const configuredSpeedTestUrl = process.env.NEXT_PUBLIC_SPEEDTEST_URL ?? process.env.SPEEDTEST_URL
    const speedTestUrl = configuredSpeedTestUrl ?? process.env.NEXT_PUBLIC_BACKEND_ENDPOINT ?? ''
    const normalizedSpeedTestUrl = speedTestUrl.endsWith('/') ? speedTestUrl : `${speedTestUrl}/`
    client.setParameter('url_dl', `${normalizedSpeedTestUrl}garbage.php`)
    client.setParameter('url_ul', `${normalizedSpeedTestUrl}empty.php`)
    client.setParameter('url_ping', `${normalizedSpeedTestUrl}empty.php`)
    client.setParameter('url_getIp', `${normalizedSpeedTestUrl}getIP.php`)
    console.debug('[SpeedTest] start', { url: normalizedSpeedTestUrl })
    client.onupdate = (data) => {
      const nextDownload = Number(data.dlStatus) || 0
      const nextUpload = Number(data.ulStatus) || 0
      const nextPing = Number(data.pingStatus) || 0
      const nextJitter = Number(data.jitterStatus) || 0
      console.debug('[SpeedTest] update', { download: nextDownload, upload: nextUpload, ping: nextPing, jitter: nextJitter, state: data.testState })
      setDownload(nextDownload)
      setUpload(nextUpload)
      setPing(nextPing)
      setJitter(nextJitter)
    }
    client.onend = () => {
      console.debug('[SpeedTest] test over')
      runningRef.current = false
      setRunning(false)
    }
    client.start()
  }

  function speedTestOutcome () {
    const faqLink = supportLinks[locale] ?? supportLinks['en-US']
    if (ping >= badLatencyLimit || jitter > badJitterLimit) {
      return t.rich('speedTestBad', {
        link: (chunks) => <a href={faqLink} target='_blank' rel='noreferrer'>{chunks}</a>
      })
    }
    if ((ping >= regularLatencyLimit && ping < badLatencyLimit) || (jitter < badJitterLimit && jitter >= jitterLimit)) {
      return t.rich('speedTestMeh', {
        faq: (chunks) => <a href={faqLink} target='_blank' rel='noreferrer'>{chunks}</a>
      })
    }
    return <p>{t('speedTestFine', { siteName: process.env.NEXT_PUBLIC_SITENAME ?? '' })}</p>
  }

  const pingClass = ping >= regularLatencyLimit && ping < badLatencyLimit
    ? styles.yellow
    : ping >= badLatencyLimit ? styles.red : ''
  const jitterClass = ping === 0 || jitter === 0 || jitter <= jitterLimit
    ? ''
    : jitter > jitterLimit && jitter < badJitterLimit ? styles.yellow : styles.red

  return (
    <div className={styles.main}>
      <h1 className={`title2 ${styles.title}`}>{t('title')}</h1>
      <div className={styles.testInfo}>
        <SpeedTestProgress type={t('download')} value={download} units={t('MegabitsXsecond')} />
        <SpeedTestProgress type={t('upload')} value={upload} units={t('MegabitsXsecond')} />
      </div>
      <div className={styles.testParams}>
        <p className={pingClass}>{t('ping', { ping: ping })}</p>
        <p className={jitterClass}>{t('jitter', { jitter: jitter })}</p>
      </div>
      <div className={styles.testControls}>
        <Button size={ButtonSize.MEDIUM} disabled={running} onClick={startTest}>{t('start')}</Button>
      </div>
      {started && <div className={styles.speedTestOutcome}>{speedTestOutcome()}</div>}
    </div>
  )
}
